import React, { useState, useEffect, useContext } from 'react';
import tokenStore from '../../utils/tokenStore';
import { Link } from 'react-router-dom';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth } from '../../utils/authUtils';
import { normalizeTierLabel } from '../../constants/membershipPackages';
import '../../css/myRelative.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const MyRelative = () => {
    const { data } = useContext(DataContext);
    const [relatives, setRelatives] = useState([]);
    const [linkKey, setLinkKey] = useState('');
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');
    const [loading, setLoading] = useState(false);
    const [unlinkTarget, setUnlinkTarget] = useState(null);
    const [unlinkSubmitting, setUnlinkSubmitting] = useState(false);
    const currentRole = String(data?.role || '').toLowerCase();
    const isRelative = currentRole === 'relative';
    const isElderly = currentRole === 'elderly';

    // Kiểm tra quyền truy cập: cho phép elderly và relative
    useEffect(() => {
        if (!data) return;
            const role = String(data.role).toLowerCase();
        if (role !== 'relative' && role !== 'elderly') {
            window.location.href = '/';
        }
    }, [data]);

    // Lấy danh sách người thân được liên kết
    useEffect(() => {
        if (data?.id) {
            fetchRelatives();
        }
    }, [data]);

    const fetchRelatives = async () => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/relatives`);
            const result = await response.json();
            if (result.success) {
                const list = result.data || [];
                let membershipMap = new Map();
                if (isRelative) {
                    try {
                        const memRes = await fetchWithAuth(`${API_BASE}/api/membership`);
                        const memJson = await memRes.json().catch(() => ({}));
                        if (memRes.ok && memJson?.success && Array.isArray(memJson.data?.linkedElderlies)) {
                            membershipMap = new Map(
                                memJson.data.linkedElderlies.map((e) => [Number(e.id), e])
                            );
                        }
                    } catch (_) {}
                }
                let checkinMap = new Map();
                try {
                    const statusRes = await fetchWithAuth(`${API_BASE}/api/daily-checkin/status`);
                    const statusPayload = await statusRes.json().catch(() => ({}));
                    if (statusRes.ok && statusPayload?.success && Array.isArray(statusPayload?.data)) {
                        checkinMap = new Map(
                            statusPayload.data.map((s) => [Number(s.id), s])
                        );
                    }
                } catch (_) {}

                const enriched = list.map((relative) => {
                    const status = checkinMap.get(Number(relative.id));
                    const mem = membershipMap.get(Number(relative.id));
                    return {
                        ...relative,
                        checkedInToday: Boolean(status?.checkedInToday),
                        consecutiveMissedDays: Number(status?.consecutiveMissedDays || 0),
                        tierLabel: normalizeTierLabel(mem?.tierLabel || 'Chưa có gói'),
                        hasMembership: Boolean(mem?.hasMembership),
                        expiresAt: mem?.expiresAt || null,
                    };
                });
                setRelatives(enriched);
            }
        } catch (error) {

        }
    };

    const handleLinkSubmit = async (e) => {
        e.preventDefault();

        if (!isRelative) {
            setMessage('Chỉ tài khoản người thân mới được nhập mã liên kết');
            setMessageType('error');
            return;
        }

        if (!linkKey.trim()) {
            setMessage('Vui lòng nhập mã kết nối');
            setMessageType('error');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/relatives/link`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenStore.getToken()}`
                },
                body: JSON.stringify({ linkKey: linkKey.trim() })
            });

            const result = await response.json();

            if (result.success) {
                setMessage('✅ Liên kết thành công!');
                setMessageType('success');
                setLinkKey('');
                fetchRelatives();
                window.dispatchEvent(new Event('tamcare:membership-updated'));
                setTimeout(() => setMessage(null), 3000);
            } else {
                setMessage(result.message || '❌ Liên kết thất bại!');
                setMessageType('error');
            }
        } catch (error) {

            setMessage('❌ Lỗi kết nối với máy chủ');
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleUnlink = async (elderly) => {
        if (!isRelative) return;
        setUnlinkTarget(elderly);
    };

    const confirmUnlink = async () => {
        if (!unlinkTarget || !isRelative) return;
        setUnlinkSubmitting(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/relatives/${unlinkTarget.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${tokenStore.getToken()}`
                }
            });

            const result = await response.json();

            if (result.success) {
                setMessage(result.message || '✅ Hủy liên kết thành công!');
                setMessageType('success');
                setUnlinkTarget(null);
                fetchRelatives();
                window.dispatchEvent(new Event('tamcare:membership-updated'));
                setTimeout(() => setMessage(null), 5000);
            } else {
                setMessage(result.message || '❌ Hủy liên kết thất bại!');
                setMessageType('error');
            }
        } catch (error) {
            setMessage('❌ Lỗi kết nối với máy chủ');
            setMessageType('error');
        } finally {
            setUnlinkSubmitting(false);
        }
    };

    const getInitial = (fullName) => {
        if (!fullName || fullName.trim() === '') return 'U';
        return fullName.trim().substring(0, 1).toUpperCase();
    };

    return (
        <main className="tamcare-main-content-relatives">
            <div className="tamcare-page-header">
                <h1>{isElderly ? 'Người thân đã liên kết' : 'Quản lý người thân'}</h1>
                <p>
                    {isElderly
                        ? 'Danh sách người thân đã liên kết với bạn qua mã kết nối.'
                        : 'Kết nối và theo dõi sức khỏe các thành viên trong gia đình.'}
                </p>
            </div>

            {/* Form liên kết chỉ dành cho relative */}
            {isRelative && (
                <div className="tamcare-link-form-card">
                    <div className="tamcare-link-icon">
                        <i className="fa-solid fa-link"></i>
                    </div>
                    <form onSubmit={handleLinkSubmit} style={{ display: 'contents' }}>
                        <input
                            type="text"
                            name="linkKey"
                            className="tamcare-input-link"
                            placeholder="Nhập mã kết nối (TC123456)..."
                            value={linkKey}
                            onChange={(e) => setLinkKey(e.target.value)}
                            disabled={loading}
                        />
                        <button
                            type="submit"
                            className="tamcare-btn-submit"
                            disabled={loading}
                        >
                            {loading ? 'Đang xử lý...' : 'Liên kết ngay'}
                        </button>
                    </form>
                </div>
            )}

            {/* Thông báo */}
            {message && (
                <div
                    className={`tamcare-alert-msg ${messageType === 'error' ? 'tamcare-alert-error' : 'tamcare-alert-success'}`}
                >
                    {message}
                </div>
            )}

            {/* Grid hiển thị người thân */}
            <div className="tamcare-relative-grid">
                {relatives && relatives.length > 0 ? (
                    relatives.map((relative) => (
                        <div key={relative.id} className="tamcare-relative-card">
                            <div className="tamcare-relative-header">
                                <div className="tamcare-initial-avatar" style={{ overflow: 'hidden' }}>
                                    {relative?.avatar ? (
                                        <img src={`${API_BASE}${relative.avatar}`} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '18px' }} />
                                    ) : (
                                        getInitial(relative.fullName)
                                    )}
                                </div>
                                <div className="tamcare-relative-info">
                                    <h3>{relative.fullName || 'Không xác định'}</h3>
                                    <p>
                                        {isRelative
                                            ? `Mã kết nối: ${relative.linkKey || relative.id}`
                                            : `Tài khoản: ${relative.username || relative.id}`}
                                    </p>
                                    {isRelative && relative.tierLabel && (
                                        <p style={{ fontSize: 12, color: relative.hasMembership ? '#16a34a' : '#94a3b8', marginTop: 4 }}>
                                            Gói: {normalizeTierLabel(relative.tierLabel)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="tamcare-relative-body">
                                {isRelative ? (
                                    <div className="tamcare-checkin-status">
                                        <span>Trạng thái hôm nay:</span>
                                        <span className={`tamcare-status-badge ${relative.checkedInToday ? 'done' : 'pending'}`}>
                                            <i className={`fa-solid ${relative.checkedInToday ? 'fa-circle-check' : 'fa-clock'}`}></i>
                                            {relative.checkedInToday ? 'ĐÃ ĐIỂM DANH' : 'CHƯA ĐIỂM DANH'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="tamcare-checkin-status">
                                        <span>Liên kết từ:</span>
                                        <span className="tamcare-status-badge done">
                                            <i className="fa-solid fa-link"></i>
                                            {relative.linkedAt ? new Date(relative.linkedAt).toLocaleDateString('vi-VN') : 'Đã liên kết'}
                                        </span>
                                    </div>
                                )}

                                <div className="tamcare-relative-actions">
                                    {isRelative ? (
                                        <>
                                            <Link
                                                to={`/profile/${relative.id}`}
                                                className="tamcare-btn-detail"
                                            >
                                                Xem chi tiết sức khỏe
                                            </Link>
                                            <button
                                                className="tamcare-btn-unlink"
                                                type="button"
                                                onClick={() => handleUnlink(relative)}
                                            >
                                                Hủy liên kết
                                            </button>
                                        </>
                                    ) : (
                                        <Link
                                            to={`/profile/${relative.id}`}
                                            className="tamcare-btn-detail"
                                        >
                                            Xem hồ sơ của tôi
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="tamcare-empty-state">
                        <i className="fa-solid fa-users-slash"></i>
                        <p>
                            {isRelative
                                ? 'Chưa có người thân nào được liên kết. Hãy nhập mã kết nối ở trên.'
                                : 'Chưa có người thân nào liên kết với tài khoản của bạn.'}
                        </p>
                    </div>
                )}
            </div>

            {unlinkTarget && (
                <div
                    className="tamcare-membership-modal"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => !unlinkSubmitting && setUnlinkTarget(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%', padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
                    >
                        <h3 style={{ margin: '0 0 12px', color: '#b45309' }}>
                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }} />
                            Xác nhận hủy liên kết
                        </h3>
                        <p style={{ color: '#475569', lineHeight: 1.6 }}>
                            Bạn sắp hủy liên kết với <strong>{unlinkTarget.fullName || 'người cao tuổi này'}</strong>.
                        </p>
                        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 14, marginBottom: 20 }}>
                            <strong style={{ color: '#c2410c' }}>Cảnh báo quan trọng:</strong>
                            <p style={{ margin: '8px 0 0', color: '#9a3412', fontSize: 14, lineHeight: 1.6 }}>
                                Gói hội viên bạn đã mua cho người này sẽ <strong>bị hủy ngay</strong> và không hoàn tiền.
                                Hãy chắc chắn trước khi tiếp tục — thao tác nhầm có thể làm mất quyền lợi đã thanh toán.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="tamcare-btn-detail"
                                disabled={unlinkSubmitting}
                                onClick={() => setUnlinkTarget(null)}
                            >
                                Không, giữ liên kết
                            </button>
                            <button
                                type="button"
                                className="tamcare-btn-unlink"
                                disabled={unlinkSubmitting}
                                onClick={confirmUnlink}
                            >
                                {unlinkSubmitting ? 'Đang xử lý...' : 'Vẫn hủy liên kết'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default MyRelative;
